import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadMaintenancePhotos, IMAGE_ACCEPT, isImage } from "@/lib/upload";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Camera, Trash2, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";

function ThumbBody({ src, failed, filename, onOpen }) {
  if (src) return <button type="button" onClick={() => onOpen(src)} className="h-full w-full"><img src={src} alt={filename} className="h-full w-full object-cover" /></button>;
  const Icon = failed ? ImageOff : Loader2;
  return <div className="h-full w-full flex items-center justify-center text-slate-400"><Icon className={`h-4 w-4 ${failed ? "" : "animate-spin"}`} /></div>;
}

function Thumb({ item, photo, onOpen, onDelete, canDelete }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    api.get(`/maintenance/${item.id}/photos/${photo.id}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setFailed(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [item.id, photo.id]);
  return (
    <div className="relative group h-20 w-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-100" data-testid={`maint-photo-${photo.id}`}>
      <ThumbBody src={src} failed={failed} filename={photo.filename} onOpen={onOpen} />
      {canDelete && (
        <button type="button" onClick={() => onDelete(photo)} data-testid={`maint-photo-delete-${photo.id}`}
          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white hidden group-hover:flex items-center justify-center hover:bg-rose-600">
          <Trash2 className="h-3 w-3" />
        </button>
      )}
      {photo.uploaded_by_role === "landlord" && <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[9px] text-center py-0.5">landlord</span>}
    </div>
  );
}

export function MaintenancePhotos({ item, role, onChange }) {
  const [open, setOpen] = useState(null);
  const [progress, setProgress] = useState(null);
  const ref = useRef(null);
  const photos = item.photos || [];
  const mine = photos.filter((p) => p.uploaded_by_role === role).length;
  const canAdd = role === "landlord" ? photos.length < 8 : mine < 4;

  const add = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => isImage(f) && f.size <= 10 * 1024 * 1024);
    e.target.value = "";
    if (!files.length) return toast.error("Only JPG, PNG or WEBP photos under 10 MB");
    const limit = role === "landlord" ? 8 - photos.length : 4 - mine;
    setProgress(1);
    try {
      await uploadMaintenancePhotos(item.id, files.slice(0, limit), setProgress);
      toast.success(`${Math.min(files.length, limit)} photo(s) added`);
      onChange?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setProgress(null); }
  };

  const del = async (photo) => {
    try { await api.delete(`/maintenance/${item.id}/photos/${photo.id}`); toast.success("Photo removed"); onChange?.(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  if (!photos.length && !canAdd) return null;

  return (
    <div className="mt-3" data-testid={`maint-photos-${item.id}`}>
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <Thumb key={p.id} item={item} photo={p} onOpen={setOpen} onDelete={del}
            canDelete={role === "landlord" || p.uploaded_by_role === "tenant"} />
        ))}
        {canAdd && (
          <button type="button" onClick={() => ref.current?.click()} disabled={progress !== null} data-testid={`maint-photo-add-${item.id}`}
            className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors disabled:opacity-60">
            {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> {progress}%</> : <><Camera className="h-5 w-5" /> Add photo</>}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={add} data-testid={`maint-photo-input-${item.id}`} />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/95 border-0" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          <DialogDescription className="sr-only">Full-size maintenance photo</DialogDescription>
          {open && <img src={open} alt="" className="max-h-[80vh] w-full object-contain rounded" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
